/**
 * deviceUnlock — orchestriert den geraete-gebundenen E2E-Zugang (#1537) fuer das
 * Outlook-Add-in (passwortloser Entra-User).
 *
 * Hintergrund: ein reiner Entra-Request traegt nur ein JWT, keine Session mit
 * gewrapptem User-Key. Damit der Workspace-Key entsperrt werden kann, braucht
 * das Add-in ein vertrautes Geraet (device_id + device_secret), das der Server
 * pro Request ueber die Header X-FD-Device-Id/-Secret verwertet
 * (WorkspaceCrypto::keyFor -> KeyService::getWorkspaceKeyViaDevice).
 *
 * Dieses Modul beschafft genau diese Creds:
 *   - bootstrap: erster Krypto-Kontakt -> mintet Identitaet, Recovery-Code (einmalig
 *     anzeigen!) und vertraut DIESEM Geraet.
 *   - trust (via Recovery-Code): es existiert bereits eine Identitaet (z.B. ueber
 *     das Web angelegt) -> dieses neue Geraet wird per Recovery-Code vertraut.
 *
 * Cross-Device Peer-Approval (Freischaltung durch ein anderes vertrautes Geraet
 * ohne Recovery-Code) existiert backend-seitig (#1401 Step 2), ist hier aber noch
 * nicht verdrahtet -- der Recovery-Code-Pfad deckt den Hardware-/Client-Wechsel ab.
 */

import { apiFetch, ApiError } from '../api/client.js'
import { getActiveAccount } from './msal.js'
import { getDeviceCreds, setDeviceCreds, clearDeviceCreds } from './deviceStore.js'

/** Ergebnis-Codes des Unlock-Flows fuer die UI. */
export const UNLOCK = {
  ALREADY: 'already', // gueltige Geraete-Creds lagen bereits vor
  BOOTSTRAPPED: 'bootstrapped', // frische Identitaet geminted -> recoveryCode MUSS gezeigt werden
  TRUSTED: 'trusted', // neues Geraet via Recovery-Code vertraut
  NEEDS_RECOVERY: 'needs_recovery', // Identitaet existiert schon -> Recovery-Code-Eingabe noetig
  HAS_PASSWORD: 'has_password', // Konto hat ein Passwort -> via Web mit Passwort entsperren
}

const DEVICE_LABEL = 'Outlook Add-in'

async function accountKey() {
  const acc = await getActiveAccount()
  return acc?.homeAccountId || null
}

/**
 * Beschafft Geraete-Creds und gibt einen UNLOCK.*-Code zurueck.
 *
 * @param {{ recoveryCode?: string, force?: boolean }} opts
 *   - recoveryCode: vorhandene Identitaet ueber Recovery-Code auf diesem Geraet vertrauen.
 *   - force: vorhandene (evtl. veraltete/widerrufene) lokale Creds verwerfen und neu beschaffen.
 */
export async function unlockEncryption({ recoveryCode, force } = {}) {
  const key = await accountKey()
  if (!key) throw new ApiError('no_account', 'Kein aktiver Microsoft-Account')

  if (force) {
    await clearDeviceCreds(key).catch(() => {})
  } else if (!recoveryCode) {
    const existing = await getDeviceCreds(key).catch(() => null)
    if (existing) return { code: UNLOCK.ALREADY, creds: existing }
  }

  // Pfad mit Recovery-Code: bestehende Identitaet auf diesem Geraet vertrauen.
  if (recoveryCode) {
    const resp = await apiFetch('/api/auth/device/trust', {
      method: 'POST',
      body: JSON.stringify({ recovery_code: recoveryCode, label: DEVICE_LABEL }),
    })
    const creds = { device_id: resp.device_id, device_secret: resp.device_secret }
    await setDeviceCreds(key, creds)
    return { code: UNLOCK.TRUSTED, creds }
  }

  // Erstkontakt: Bootstrap (mintet Identitaet + Recovery-Code + vertraut dieses Geraet).
  try {
    const resp = await apiFetch('/api/auth/device/bootstrap', {
      method: 'POST',
      body: JSON.stringify({ label: DEVICE_LABEL }),
    })
    const creds = { device_id: resp.device_id, device_secret: resp.device_secret }
    await setDeviceCreds(key, creds)
    return { code: UNLOCK.BOOTSTRAPPED, creds, recoveryCode: resp.recovery_code }
  } catch (err) {
    if (err instanceof ApiError && err.code === 'identity_exists') {
      return { code: UNLOCK.NEEDS_RECOVERY }
    }
    if (err instanceof ApiError && err.code === 'has_password') {
      return { code: UNLOCK.HAS_PASSWORD }
    }
    throw err
  }
}
